import { ProductType } from '@/types/types';

// Voss only exposes Id / Name / ArticleNumber for a product, and for the BokaMera package
// the name equals the article code (e.g. "BM.EXTRA.SUPPORT"). This map adds the human
// meaning of the known codes so the superadmin knows what a quantity unit is before
// posting usage. Codes are seeded in legacy-api (VossConstants / VossApiClientManager).
// Unknown codes fall back to the raw name, so a new Voss product still works.

export type ArticleInfo = {
    title: string;
    description: string;
    /** What one unit of Quantity represents, e.g. "15 minutes". */
    unit: string;
};

const YEARLY_SUFFIX = '.YR';

const KNOWN_ARTICLES: Record<string, ArticleInfo> = {
    'BM.EXTRA.SUPPORT': {
        title: 'Extra support',
        description: 'Support work that is not included in the plan.',
        unit: '15 minutes',
    },
    'BM.DEV.CUSTOM': {
        title: 'Development / customization',
        description: 'Custom development or configuration work ordered by the customer.',
        unit: '60 minutes',
    },
    'BM.WT.FREE': {
        title: 'Walkthrough for free accounts',
        description: 'Guided walkthrough session for customers on the free plan.',
        unit: '60 minutes',
    },
    'BM.SMS': {
        title: 'SMS (level 1)',
        description: 'Sent SMS messages at the level 1 rate.',
        unit: '1 SMS',
    },
    'BM.SMS2': {
        title: 'SMS (level 2)',
        description: 'Sent SMS messages at the level 2 rate.',
        unit: '1 SMS',
    },
    'BM.BOOK.FEE': {
        title: 'Booking fee',
        description: "Bookings above the plan's monthly free allowance.",
        unit: '1 booking',
    },
    'BM.API.LICENS': {
        title: 'API license',
        description: 'Add-on license for API access.',
        unit: '1 license',
    },
    'BM.DOM.W.REBATE': {
        title: 'Own domain license (with rebate)',
        description: 'Add-on license for a custom domain, rebated price.',
        unit: '1 license',
    },
    'BM.DOM.WO.REBATE': {
        title: 'Own domain license (without rebate)',
        description: 'Add-on license for a custom domain, full price.',
        unit: '1 license',
    },
    'BM.SYNC.CODE.LICENS': {
        title: 'Sync to code lock license',
        description: 'Add-on license for syncing bookings to a code lock.',
        unit: '1 license',
    },
    'BM.DISPLAY.LICENS': {
        title: 'Display license',
        description: 'Add-on license for the display / signage view.',
        unit: '1 license',
    },
    'BM.FREE.LICENS': {
        title: 'Free plan',
        description: 'Main product of the Free tier.',
        unit: '1 license',
    },
    'BM.START.LICENS': {
        title: 'Start plan',
        description: 'Main product of the Start tier.',
        unit: '1 license',
    },
    'BM.SMART.LICENS': {
        title: 'Smart plan',
        description: 'Main product of the Smart tier.',
        unit: '1 license',
    },
    'BM.PRO.LICENS': {
        title: 'Pro plan',
        description: 'Main product of the Pro tier.',
        unit: '1 license',
    },
    'BM.ENTER.LICENS': {
        title: 'Enterprise plan',
        description: 'Main product of the Enterprise tier.',
        unit: '1 license',
    },
};

export const getArticleInfo = (articleNumber?: string | null): ArticleInfo | undefined => {
    if (!articleNumber) return undefined;

    const code = articleNumber.trim().toUpperCase();
    const direct = KNOWN_ARTICLES[code];
    if (direct) return direct;

    if (code.endsWith(YEARLY_SUFFIX)) {
        const base = KNOWN_ARTICLES[code.slice(0, -YEARLY_SUFFIX.length)];
        if (base) return { ...base, title: `${base.title} (yearly plan)` };
    }

    return undefined;
};

export type VossProductLike = { ArticleNumber?: string; Name?: string };

/** Dropdown label: "CODE – Human title", or the raw name when the code is unknown. */
export const formatArticleOption = (product: VossProductLike): string => {
    const code = product.ArticleNumber ?? '';
    const name = product.Name ?? code;
    const info = getArticleInfo(code);

    if (info) return `${code} – ${info.title} (per ${info.unit})`;
    if (code && name && code !== name) return `${code} – ${name}`;

    return name || code;
};

export const PRODUCT_TYPE_BILLING: Record<ProductType, string> = {
    [ProductType.Usage]:
        'Usage product: billed in the usage billing period. The quantity is reset every period.',
    [ProductType.License]:
        'License product: billed in the subscription billing period. The quantity is carried over to the next period.',
    [ProductType.Addon]: 'Add-on product: billed in the subscription billing period.',
    [ProductType.Main]:
        'Main product of a product group: billed in the subscription billing period.',
};
